import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Grid, FormControl, Select, MenuItem, Box, InputLabel } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import httpClient from "../../lib/httpClient";
import { fixedFormatter, percent100twoDecimalsFormatter } from "../util/tableUtils";
import { userHasSomePermissions } from "../util/permissionsUtils";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { CysButtonPopover } from "../util/CysButtonPopover";
import { useClientConfig } from "../../context/clientConfigContext";

import { PermissionIdEnum } from "../util/types/permissions/permissionIdEnum";

const companyCodeMapping = {
  RMA: "001",
  ITA: "003",
};

const locationConfidenceMapping = {
  1: "=",
  "<1": "<",
};

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 190,
    marginRight: 30,
  },
}));

const tooltipInfoMsg = (
  <Box m={1}>
    This table summarizes risk statistics of
    <ul>
      <li> All building contract locations, </li>
      <li> which had a complete address provided, </li>
      <li> with any garanzie, </li>
      <li> with a nonzero TIV, </li>
      <li> that were active on the 11/11/2025,</li>
      <li> The risk is computed for every location separately.</li>
      <li> The TIV of the contract is applied for every location.</li>
    </ul>
  </Box>
);

export const NatcatStatistics = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [natcatStatistics, setNatcatStatistics] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [company, setCompany] = useState("RMA+ITA");
  const [locationConfidence, setLocationConfidence] = useState("Any");
  const [risk, setRisk] = useState("Not selected");
  const config = useClientConfig();

  const fetchData = useCallback(async () => {
    if (!locationConfidence || !company) return;
    setIsLoading(true);

    const companyPayload = () => {
      const companyCode = companyCodeMapping[company];
      return companyCode ? [{ companyCode: { value: companyCode, operator: "equals" } }] : [];
    };

    const locationConfidencePayload = () => {
      const operator = locationConfidenceMapping[locationConfidence];
      return operator ? [{ cys_loc_confidence: { value: 1, operator: operator } }] : [];
      // Note: for this to work cys_loc_confidence must be in the list of exceptions exceptionNumberFields in the backend
    };

    const payload = { filters: JSON.stringify([...companyPayload(), ...locationConfidencePayload()]) };
    try {
      const response = await httpClient().post("/natcat/createStatistics", payload);

      if (!response.status === 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setNatcatStatistics(response.data);
    } catch (error) {
      console.error("An error occurred:", error);
      alert("An error occurred. Please try again later." + error);
    } finally {
      setIsLoading(false);
    }
  }, [company, locationConfidence]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangeCompany = (event) => {
    setCompany(event.target.value);
  };
  const handleChangeLC = (event) => {
    setLocationConfidence(event.target.value);
  };

  const handleChangeRisk = (event) => {
    setRisk(event.target.value);
  };

  const statisticColumns = [
    { field: "levelName", headerName: t("climateAnalyticsTabs.riskLevels") },
    {
      field: "postrenewalValueFinal",
      headerName: t("climateAnalyticsSelects.postrenewalValueFinal"),
      ...fixedFormatter,
    },
    {
      field: "count",
      headerName: t("climateAnalyticsSelects.policyNumber"),
    },
    {
      field: "percentOfTotal",
      headerName: "% of TIV",
      ...percent100twoDecimalsFormatter,
    },
  ];

  const columns = statisticColumns.map((column) => ({
    flex: 1,
    headerAlign: "right",
    align: "right",
    ...column,
  }));

  return (
    <Box mt={2} mb={1}>
      <Grid item xs={12} sm={8}>
        {/*  TODO: extract formControl into a separate component (use it in the analytics module as well) */}

        {config.showNatCatAnalyticsFilter && (
          <>
            <FormControl className={classes.formControl}>
              <InputLabel>Select a company</InputLabel>
              <Select value={company} onChange={handleChangeCompany}>
                <MenuItem value="RMA">RMA only</MenuItem>
                <MenuItem value="ITA">ITA only</MenuItem>
                <MenuItem value="RMA+ITA">RMA+ITA </MenuItem>
              </Select>
            </FormControl>
            <FormControl className={classes.formControl} mr={3}>
              <InputLabel style={{ width: "300px" }}>Select confidence level</InputLabel>
              <Select value={locationConfidence} onChange={handleChangeLC}>
                <MenuItem value="1">1.00 (highest confidence)</MenuItem>
                <MenuItem value="<1">Under 1.00 (less than highest)</MenuItem>
                <MenuItem value="Any">Any</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        <FormControl className={classes.formControl}>
          <InputLabel>Select risk</InputLabel>
          <Select value={risk} onChange={handleChangeRisk}>
            <MenuItem value="Not selected">Select one</MenuItem>
            {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.LandslideRiskIspra]) && (
              <MenuItem value="landslideStatistics">Landslide ISPRA</MenuItem>
            )}

            {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.InlandFloodHowden]) && (
              <MenuItem value="howdenInlandFloodStatistics">Inland Flood (Howden) (old)</MenuItem>
            )}

            {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.InlandFloodHowden56]) && (
              <MenuItem value="howden56InlandFloodStatistics">Inland Flood (Howden) 2</MenuItem>
            )}

            {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.SeismicRiskHowden2]) && (
              <MenuItem value="howdenSeismicStatistics">Seismic Risk Howden</MenuItem>
            )}

            {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.InlandFloodIspra]) && (
              <MenuItem value="inlandFloodStatistics">Inland Flood ISPRA</MenuItem>
            )}
            {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.SeismicRiskIspra]) && (
              <MenuItem value="seismicStatistics">Seismic Risk ISPRA</MenuItem>
            )}
          </Select>
        </FormControl>

        <Box mt={2}>
          <DataGridCystellarTable
            rows={natcatStatistics[risk] || []}
            columns={columns}
            getRowId={(rowData) => rowData._id}
            density="compact"
            loading={isLoading}
            disableColumnFilter={true}
            pagination={false}
            customNoRowsMsg="Please select the filters above"
            customToolbarComp={true}
            customToolbarWidgets={
              <CysButtonPopover buttonMsg="table info" popoverText={tooltipInfoMsg}></CysButtonPopover>
            }
          />
        </Box>
      </Grid>
    </Box>
  );
};
